import {create} from 'zustand';

export interface ProcessingPreview {
  url: string;
  mediaType: 'image' | 'video';
}

function previewMediaType(file: File): ProcessingPreview['mediaType'] | null {
  if (file.type.startsWith('image/')) return 'image';
  if (file.type.startsWith('video/')) return 'video';
  return null;
}

interface ProcessingPreviewStore {
  bySubmissionId: Record<string, ProcessingPreview>;
  remember: (submissionId: string, file: File) => void;
  forget: (submissionId: string) => void;
  forgetAvailable: (submissionIds: string[]) => void;
}

export function submissionIdFromMutation(result: unknown): string | null {
  if (!result || typeof result !== 'object' || !('data' in result)) {
    return null;
  }
  const {data} = result as {data?: {id?: unknown}};
  return typeof data?.id === 'string' ? data.id : null;
}

export const useProcessingPreviewStore = create<ProcessingPreviewStore>(
  (set, get) => ({
    bySubmissionId: {},
    remember: (submissionId, file) => {
      const mediaType = previewMediaType(file);
      if (!mediaType) {
        get().forget(submissionId);
        return;
      }
      const previous = get().bySubmissionId[submissionId];
      if (previous) URL.revokeObjectURL(previous.url);
      const url = URL.createObjectURL(file);
      set((state) => ({
        bySubmissionId: {
          ...state.bySubmissionId,
          [submissionId]: {url, mediaType},
        },
      }));
    },
    forget: (submissionId) => {
      const previous = get().bySubmissionId[submissionId];
      if (!previous) return;
      URL.revokeObjectURL(previous.url);
      set((state) => {
        const next = {...state.bySubmissionId};
        delete next[submissionId];
        return {bySubmissionId: next};
      });
    },
    forgetAvailable: (submissionIds) => {
      if (submissionIds.length === 0) return;
      const ids = submissionIds.filter(
        (submissionId) => get().bySubmissionId[submissionId]
      );
      if (ids.length === 0) return;
      set((state) => {
        const next = {...state.bySubmissionId};
        ids.forEach((submissionId) => {
          const preview = next[submissionId];
          if (!preview) return;
          URL.revokeObjectURL(preview.url);
          delete next[submissionId];
        });
        return {bySubmissionId: next};
      });
    },
  })
);
